import { useState } from 'react';
import { toast } from 'sonner';
import { ListOrdered, Plus, ChevronUp, ChevronDown, Trash2, Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Carte, TitreCarte, nouveauPoint } from './constantes';
import { SuggestionsOdj } from './SuggestionsOdj';
import { BoutonModeleFisl } from './ModeleFisl';

const BoutonIcone = ({ onClick, disabled, title, testId, children, danger }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} data-testid={testId}
    className={`rounded-lg p-1.5 text-slate-400 transition-colors disabled:opacity-30 ${danger ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-white hover:text-slate-700'}`}>{children}</button>
);

const PointEditeur = ({ p, i, n, maj, deplacer, retirer }) => (
  <li className="rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid={`cp-point-${i}`}>
    <div className="flex items-start gap-3">
      <span className="mt-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0F172A] text-xs font-bold text-white">{i + 1}</span>
      <div className="min-w-0 flex-1 space-y-2">
        <Input value={p.titre} onChange={(e) => maj(i, 'titre', e.target.value)} placeholder="Intitulé du point" className="bg-white font-medium" data-testid={`cp-point-titre-${i}`} />
        <Textarea value={p.details || ''} onChange={(e) => maj(i, 'details', e.target.value)} rows={2} className="bg-white text-sm"
          placeholder="Précisions (facultatif, imprimées sur la convocation)" data-testid={`cp-point-details-${i}`} />
      </div>
      <div className="flex flex-col">
        <BoutonIcone onClick={() => deplacer(i, -1)} disabled={i === 0} title="Monter" testId={`cp-point-monter-${i}`}><ChevronUp className="h-4 w-4" /></BoutonIcone>
        <BoutonIcone onClick={() => deplacer(i, 1)} disabled={i === n - 1} title="Descendre" testId={`cp-point-descendre-${i}`}><ChevronDown className="h-4 w-4" /></BoutonIcone>
        <BoutonIcone onClick={() => retirer(i)} title="Supprimer" testId={`cp-point-supprimer-${i}`} danger><Trash2 className="h-4 w-4" /></BoutonIcone>
      </div>
    </div>
  </li>
);

const empreinte = (pts) => JSON.stringify(pts.map((p) => [p.titre, p.details || '', p.theme || '']));

export const OngletOrdreDuJour = ({ cp }) => {
  const { conseil, enregistrer } = cp;
  const [points, setPoints] = useState(() => conseil.ordre_du_jour.map((p) => ({ ...p })));
  const [envoi, setEnvoi] = useState(false);
  const modifie = empreinte(points) !== empreinte(conseil.ordre_du_jour);
  const maj = (i, champ, v) => setPoints((ps) => ps.map((p, j) => (j === i ? { ...p, [champ]: v } : p)));
  const deplacer = (i, d) => setPoints((ps) => { const n = [...ps]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const ajouter = (titre, details, theme) => setPoints((ps) => [...ps, nouveauPoint(titre, details, theme)]);
  const sauver = async () => {
    if (points.some((p) => !p.titre.trim())) return toast.error('Chaque point doit avoir un intitulé');
    setEnvoi(true);
    const data = await enregistrer({ ordre_du_jour: points }, "Ordre du jour enregistré");
    if (data) setPoints(data.ordre_du_jour.map((p) => ({ ...p })));
    return setEnvoi(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
      <Carte data-testid="cp-odj">
        <TitreCarte Icone={ListOrdered} titre="Ordre du jour" sousTitre="Partez du modèle FISL, saisissez vos points librement ou ajoutez les propositions. Les précisions sont imprimées sur la convocation.">
          <BoutonModeleFisl annee={conseil.annee} points={points} onChange={setPoints} />
        </TitreCarte>
        {points.length === 0 && <p className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="cp-odj-vide">Aucun point pour l'instant</p>}
        <ol className="space-y-3">
          {points.map((p, i) => <PointEditeur key={p.point_id || `n${i}`} p={p} i={i} n={points.length} maj={maj} deplacer={deplacer} retirer={(k) => setPoints((ps) => ps.filter((_, j) => j !== k))} />)}
        </ol>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={() => ajouter()} data-testid="cp-odj-ajouter"><Plus className="mr-2 h-4 w-4" /> Point libre</Button>
          <Button onClick={sauver} disabled={envoi || !modifie} className="rounded-full bg-[#0F172A] px-6" data-testid="cp-odj-enregistrer">
            {envoi ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Enregistrer l'ordre du jour
          </Button>
          {modifie && <span className="text-xs font-medium text-amber-600" data-testid="cp-odj-modifie">Modifications non enregistrées</span>}
        </div>
      </Carte>
      <SuggestionsOdj annee={conseil.annee} points={points} onAjouter={ajouter} />
    </div>
  );
};
